import { loadBusinessEntityOptions } from '@/api/coupon/businessEntity'

/**
 * 优惠券模板 typeId 单位（与后端 DiscountType / 管理端 coupon_type 字典一致）
 * 1=金额(元) 2=时长(小时) 3=折扣(折) 4=全免
 */
export function formatCouponUnit(typeId) {
  const type = Number(typeId)
  if (type === 1) return '元'
  if (type === 2) return '小时'
  if (type === 3) return '折'
  if (type === 4) return ''
  return '元'
}

/** 是否可在支付/列表中选用（未使用、未过期、已领取） */
export function isCouponAvailableForUse(item) {
  if (!item) return false
  const status = Number(item.status)
  if (status === 3 || status === 4 || status === 5) return false

  const now = Date.now()
  const end = item.validEndTime || item.invalidTime
  if (end && new Date(end).getTime() <= now) return false
  if (item.validStartTime && new Date(item.validStartTime).getTime() > now) return false

  // 2=已领取；1+userId 兼容商城发券历史数据（status 误写为 1）
  if (status === 2) return true
  if (status === 1 && item.userId) return true
  return false
}

// Cache for business entity data to avoid repeated API calls
const entityCache = new Map();
const cacheExpiry = 5 * 60 * 1000; // 5 minutes cache

/**
 * Get cached entity data or fetch from API
 */
async function getCachedEntityData(businessType) {
  const cacheKey = `businessType_${businessType}`;
  const cached = entityCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < cacheExpiry) {
    console.log(`Using cached entity data for business type ${businessType}`);
    return cached.data;
  }
  
  console.log(`Fetching fresh entity data for business type ${businessType}`);
  const response = await loadBusinessEntityOptions(businessType);
  const data = response?.data || [];
  
  // Cache the result
  entityCache.set(cacheKey, {
    data: data,
    timestamp: Date.now()
  });
  
  return data;
}

/**
 * Populate busiEntityName for coupon objects based on their businessType and busiEntityId
 * @param {Object|Array} coupons - A single coupon object or array of coupon objects
 * @returns {Promise} - Promise that resolves when all busiEntityNames are populated
 */
export async function populateBusiEntityNames(coupons) {
  if (!coupons) return Promise.resolve(coupons);
  
  console.log("Starting entity name population for", Array.isArray(coupons) ? coupons.length : 1, "coupons");
  
  // Handle both single object and array cases
  const couponArray = Array.isArray(coupons) ? coupons : [coupons];
  
  // Group coupons by business type to minimize API calls
  const couponsByType = {};
  
  couponArray.forEach(coupon => {
    if (coupon.businessType && coupon.busiEntityId) {
      if (!couponsByType[coupon.businessType]) {
        couponsByType[coupon.businessType] = [];
      }
      couponsByType[coupon.businessType].push(coupon);
    }
  });
  
  // Process each business type with caching
  const promises = Object.keys(couponsByType).map(async (businessType) => {
    try {
      const entities = await getCachedEntityData(businessType);
      console.log(`Got ${entities.length} entities for business type ${businessType}`);
      
      const couponsOfType = couponsByType[businessType];
      
      // Match entities with coupons by ID
      couponsOfType.forEach(coupon => {
        const targetId = String(coupon.busiEntityId);
        
        // Try multiple matching strategies
        let entity = null;
        
        // 1. Direct string comparison
        entity = entities.find(e => String(e.id) === targetId);
        
        // 2. Try trimming any whitespace
        if (!entity) {
          entity = entities.find(e => String(e.id).trim() === targetId.trim());
        }
        
        // 3. Try numeric comparison
        if (!entity) {
          try {
            const numericId = Number(targetId);
            entity = entities.find(e => Number(e.id) === numericId);
          } catch (err) { /* Ignore conversion errors */ }
        }
        
        // 4. Check if the id is contained within the entity id (partial match)
        if (!entity) {
          entity = entities.find(e => String(e.id).includes(targetId) || targetId.includes(String(e.id)));
        }
        
        // 5. Special check for specific ID format issues
        if (!entity && businessType === '4') {
          // For property entities, try checking communityId if available
          entity = entities.find(e => e.communityId === targetId || String(e.communityId) === targetId);
        }
        
        if (entity) {
          // For parking entities
          if (parseInt(businessType) === 1) {
            coupon.busiEntityName = entity.spaceName || entity.name || entity.parkingName || entity.title;
          } 
          // For property/community entities
          else if (parseInt(businessType) === 4) {
            coupon.busiEntityName = entity.communityName || entity.name || entity.title;
            
            // If still null, try any property that might contain a name
            if (!coupon.busiEntityName) {
              for (const key in entity) {
                if (typeof entity[key] === 'string' && 
                    (key.toLowerCase().includes('name') || key.toLowerCase().includes('title'))) {
                  coupon.busiEntityName = entity[key];
                  break;
                }
              }
            }
          }
          // Default fallback
          else {
            coupon.busiEntityName = entity.name || '';
          }
          
          // Last resort - generate a name from the entity type if still null
          if (!coupon.busiEntityName) {
            coupon.busiEntityName = `Entity #${entity.id}`;
          }
        } else {
          console.warn(`No matching entity found for ID ${targetId}`);
          
          // Set a fallback name if no match is found
          coupon.busiEntityName = `未知场所 (ID: ${targetId.substring(0, 8)}...)`;
        }
      });
    } catch (error) {
      console.error(`Error fetching entity names for business type ${businessType}:`, error);
    }
  });
  
  await Promise.all(promises);
  console.log("Finished populating entity names");
  
  // Return the original format
  return Array.isArray(coupons) ? couponArray : couponArray[0];
}

/**
 * Clear the entity cache (useful for testing or when data becomes stale)
 */
export function clearEntityCache() {
  entityCache.clear();
  console.log("Entity cache cleared");
}
